import type { AppState, ScientificFrame } from '../science/types';
import type { EraVisualPackage } from '../eras/types';
import type { SceneController } from './SceneController';
import { getEra } from '../data/eras';

export class CanvasFieldFallback implements SceneController {
  private host: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private state: Readonly<AppState> | null = null;
  private frame: ScientificFrame | null = null;
  private animationId = 0;
  private resizeObserver: ResizeObserver;
  private resizeFrame = 0;
  private lastWidth = 0;
  private lastHeight = 0;
  private points: Array<{ x: number; y: number; z: number; phase: number }> = [];
  private visualTime = 0;
  private lastTimestamp = 0;
  private pendingStep = 0;
  private visualProfile: EraVisualPackage | null = null;

  constructor(host: HTMLElement) {
    this.host = host;
    this.canvas = document.createElement('canvas');
    this.canvas.setAttribute('aria-label', 'Two-dimensional fallback for the band-limited electromagnetic field visualization');
    this.canvas.className = 'field-canvas';
    const context = this.canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D context unavailable.');
    this.ctx = context;
    this.host.append(this.canvas);
    let seed = 8192;
    const random = (): number => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 4294967296;
    };
    for (let i = 0; i < 800; i += 1) {
      this.points.push({
        x: (random() - 0.5) * 2,
        y: (random() - 0.5) * 2,
        z: (random() - 0.5) * 2,
        phase: random() * Math.PI * 2
      });
    }
    this.resizeObserver = new ResizeObserver(() => this.queueResize());
    this.resizeObserver.observe(this.host);
    this.queueResize();
    this.animate();
  }

  private queueResize(): void {
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => this.resize());
  }

  private resize(): void {
    const ratio = Math.min(devicePixelRatio || 1, 2);
    const rect = this.host.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    if (width === this.lastWidth && height === this.lastHeight) return;
    this.lastWidth = width;
    this.lastHeight = height;
    this.canvas.width = Math.floor(width * ratio);
    this.canvas.height = Math.floor(height * ratio);
    this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  setVisualProfile(profile: EraVisualPackage): void {
    this.visualProfile = profile;
    this.host.dataset.apparatusKind = profile.apparatusKind;
    this.queueResize();
  }

  update(state: Readonly<AppState>, frame: ScientificFrame): void {
    this.state = state;
    this.frame = frame;
  }

  setTool(): void {
    // The fallback view has a fixed camera.
  }

  step(deltaSeconds = 1 / 30): void {
    this.pendingStep += Math.max(0, deltaSeconds);
  }

  snapshot(): string {
    return this.canvas.toDataURL('image/png');
  }

  private animate = (timestamp = 0): void => {
    this.animationId = requestAnimationFrame(this.animate);
    const state = this.state;
    const frame = this.frame;
    const width = this.host.clientWidth;
    const height = this.host.clientHeight;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, width, height);
    const gradient = ctx.createRadialGradient(width * 0.55, height * 0.43, 10, width * 0.55, height * 0.43, Math.max(width, height) * 0.65);
    gradient.addColorStop(0, 'rgba(19, 61, 105, .45)');
    gradient.addColorStop(1, 'rgba(0, 5, 12, .96)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    if (!state || !frame) return;
    const era = getEra(state.era);
    const palette = this.visualProfile?.fieldPalette ?? [era.accent, era.accentSoft, '#ffb05d'];
    const delta = this.lastTimestamp ? Math.min(0.08, (timestamp - this.lastTimestamp) / 1000) : 0;
    this.lastTimestamp = timestamp;
    const reducedFactor = state.visualization.reducedMotion ? 0.18 : 1;
    if (!state.visualization.animationPaused) this.visualTime += delta * state.visualization.animationSpeed * reducedFactor;
    if (this.pendingStep > 0) { this.visualTime += this.pendingStep; this.pendingStep = 0; }
    const time = this.visualTime;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (const point of this.points) {
      const wobble = Math.sin(time * 0.7 + point.phase + point.z * 3) * 0.06 * frame.displayAmplitude;
      const perspective = 1 / (1.7 + point.z * 0.4);
      const x = width * 0.56 + (point.x + wobble) * width * 0.34 * perspective;
      const y = height * 0.47 + (point.y + Math.cos(time + point.phase) * 0.035) * height * 0.52 * perspective;
      const radius = Math.max(0.4, (1.5 + point.z) * 1.4 + state.visualization.gain * 0.08);
      ctx.globalAlpha = 0.18 + (point.z + 1) * 0.22;
      ctx.fillStyle = point.phase > Math.PI ? palette[1] : palette[0];
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    ctx.strokeStyle = era.accent;
    ctx.globalAlpha = 0.7;
    ctx.lineWidth = 1;
    ctx.strokeRect(width * 0.26, height * 0.25, width * 0.18, height * 0.34);
    ctx.strokeStyle = era.accentSoft;
    ctx.strokeRect(width * 0.66, height * 0.31, width * 0.18, height * 0.34);
    ctx.setLineDash([6, 5]);
    ctx.strokeStyle = '#f1b75d';
    ctx.beginPath();
    ctx.moveTo(width * 0.44, height * 0.42);
    ctx.lineTo(width * 0.66, height * 0.48);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(220, 235, 247, .76)';
    ctx.font = '11px ui-monospace, monospace';
    ctx.fillText('2D compatibility renderer — Three.js unavailable or disabled', width * 0.33, height - 18);
  };

  dispose(): void {
    cancelAnimationFrame(this.animationId);
    cancelAnimationFrame(this.resizeFrame);
    this.resizeObserver.disconnect();
    this.host.replaceChildren();
  }
}
